/**
 * =============================================================================
 * NAVBAR - Barra de Navegação Fixa
 * =============================================================================
 *
 * Barra de navegação fixa no topo com glassmorphism, links principais,
 * dropdown de "Livro", menu móvel e alternador de tema.
 */

import { useState, useEffect, Fragment } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, BookOpen } from "lucide-react";
import { ROUTES } from "@/constants/routes";

const navLinks = [
  { to: ROUTES.home, label: "Início" },
  { to: ROUTES.hipnoterapia, label: "Hipnoterapia" },
  { to: ROUTES.superAprendizagem, label: "Super aprendizagem" },
  { to: ROUTES.formacao, label: "Formação" },
  { to: ROUTES.proBono, label: "Pro bono" },
  { to: ROUTES.psicologia, label: "Psicologia" },
  { to: ROUTES.curriculo, label: "Currículo" },
];

const dropdownLinks = [
  { to: ROUTES.livroManual, label: "Manual para Professores" },
  { to: ROUTES.livroSerMaior, label: "Ser Maior" },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = () => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-pureWhite/95 backdrop-blur-lg shadow-soft"
            : "bg-pureWhite/80 backdrop-blur-md"
        }`}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-[72px]">
            <Link
              to={ROUTES.home}
              className="flex items-center gap-3"
              onClick={handleLinkClick}
            >
              <img
                src="/images/logo-oficina-da-mente.png"
                alt="Logótipo Oficina da Mente"
                className="h-11 w-11 sm:h-12 sm:w-12 object-contain flex-shrink-0"
                width={48}
                height={48}
              />
              <span className="flex flex-col items-start">
                <span className="font-playfair text-lg sm:text-xl font-bold text-deepOcean tracking-wide">
                  PEDRO <span className="font-normal italic">de</span> FREITAS
                </span>
                <span className="text-[10px] sm:text-xs tracking-[0.3em] text-oceanBlue uppercase font-medium -mt-1">
                  Oficina da Mente
                </span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Fragment key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isActive
                          ? "text-oceanBlue bg-oceanBlue/10"
                          : "text-textBody hover:text-oceanBlue hover:bg-seaMist"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>

                  {link.to === ROUTES.formacao && (
                    <div
                      className="relative"
                      onMouseEnter={() => setDropdownOpen(true)}
                      onMouseLeave={() => setDropdownOpen(false)}
                    >
                      <button
                        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          dropdownOpen
                            ? "text-oceanBlue bg-oceanBlue/10"
                            : "text-textBody hover:text-oceanBlue hover:bg-seaMist"
                        }`}
                        aria-expanded={dropdownOpen}
                        aria-haspopup="true"
                      >
                        <BookOpen size={16} aria-hidden="true" />
                        Livro
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${
                            dropdownOpen ? "rotate-180" : ""
                          }`}
                          aria-hidden="true"
                        />
                      </button>

                      {dropdownOpen && (
                        <div className="absolute top-full left-0 pt-1 w-64">
                          <div className="bg-pureWhite rounded-xl shadow-lg border border-seaMist py-2 overflow-hidden">
                            {dropdownLinks.map((link) => (
                              <Link
                                key={link.to}
                                to={link.to}
                                className="block px-4 py-3 text-sm text-textBody hover:bg-seaMist hover:text-oceanBlue transition-colors"
                                onClick={handleLinkClick}
                              >
                                {link.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>

            <div className="flex items-center gap-2 lg:hidden">
              <button
                className="p-2 rounded-lg text-deepOcean hover:bg-seaMist transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-pureWhite pt-[72px] overflow-y-auto lg:hidden">
          <div className="container-main py-6">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Fragment key={link.to}>
                  <NavLink
                    to={link.to}
                    onClick={handleLinkClick}
                    className={({ isActive }) =>
                      `px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                        isActive
                          ? "text-oceanBlue bg-oceanBlue/10"
                          : "text-textBody hover:bg-seaMist"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>

                  {link.to === ROUTES.formacao && (
                    <div className="mt-4 pt-4 border-t border-paleSky">
                      <p className="px-4 text-xs uppercase tracking-wider text-textMuted mb-2">
                        Livro
                      </p>
                      {dropdownLinks.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={handleLinkClick}
                          className="flex items-center gap-2 px-4 py-3 text-base text-textBody hover:bg-seaMist rounded-lg transition-colors"
                        >
                          <BookOpen size={16} className="text-oceanBlue" aria-hidden="true" />
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
